/**
 * data/brokerCatalog.ts
 * Light broker metadata for fast selection and filtering
 * 
 * Contains minimal data needed for broker discovery phase:
 * - Names and identifiers
 * - Asset class support 
 * - Account type and prop firm filtering
 * - Popularity indicators
 * 
 * Heavy fee configuration data is loaded separately from brokerFeeData.ts
 */

import type { BrokerCatalogEntry } from '@/types/reference/BrokerCatalog';
import { GENERAL_BROKER_ACCOUNT_TYPES } from '@/types/reference/BrokerCatalog';
import type { AssetClassType } from '@/types/shared/AssetClass';

/**
 * Light broker selection metadata catalog
 * 
 * Optimized for fast filtering and broker discovery.
 * Fee details loaded separately when needed.
 */

export const BROKER_CATALOG_DATA: BrokerCatalogEntry[] = [
  {
    id: 'admiral-markets-trade',
    name: 'Admiral Markets (Trade Account)',
    shortName: 'Admiral Trade',
    assetClasses: ['forex' as const satisfies AssetClassType, 'cfd' as const satisfies AssetClassType, 'stocks' as const satisfies AssetClassType, 'stocks' as const satisfies AssetClassType, 'futures' as const satisfies AssetClassType, 'crypto' as const satisfies AssetClassType],
    accountType: GENERAL_BROKER_ACCOUNT_TYPES,
    isPopular: true,
    parentBroker: 'admiral-markets',
    notes: 'Comprehensive CFD broker with spread-only forex, commission on stocks, overnight financing on all positions',
  },

  {
    id: 'tradovate-personal-free',
    name: 'Tradovate Personal (Free Plan)',
    shortName: 'Tradovate Free',
    assetClasses: ['futures' as const satisfies AssetClassType],
    accountType: GENERAL_BROKER_ACCOUNT_TYPES,
    isPopular: true,
    parentBroker: 'tradovate',
    notes: 'Futures-only broker with free platform access and pay-as-you-trade model. Exchange, clearing, and NFA fees apply separately.',
  },

  {
    id: 'tradovate-personal-monthly',
    name: 'Tradovate Personal (Monthly Plan)',
    shortName: 'Tradovate Monthly',
    assetClasses: ['futures' as const satisfies AssetClassType],
    accountType: GENERAL_BROKER_ACCOUNT_TYPES,
    isPopular: false,
    parentBroker: 'tradovate',
    notes: 'Futures-only broker with monthly subscription plan providing reduced commission rates. $99/month subscription fee plus exchange, clearing, and NFA fees.',
  },

  {
    id: 'tradovate-personal-lifetime',
    name: 'Tradovate Personal (Lifetime Plan)',
    shortName: 'Tradovate Lifetime',
    assetClasses: ['futures' as const satisfies AssetClassType],
    accountType: GENERAL_BROKER_ACCOUNT_TYPES,
    isPopular: false,
    parentBroker: 'tradovate',
    notes: 'Futures-only broker with lifetime plan providing lowest commission rates. One-time $3,200 lifetime fee plus exchange, clearing, and NFA fees.',
  },

  {
    id: 'interactive-brokers-pro-usa',
    name: 'Interactive Brokers Pro (USA)',
    shortName: 'IBKR Pro USA',
    assetClasses: ['stocks' as const satisfies AssetClassType, 'options' as const satisfies AssetClassType, 'futures' as const satisfies AssetClassType, 'forex' as const satisfies AssetClassType, 'bonds' as const satisfies AssetClassType, 'crypto' as const satisfies AssetClassType],
    accountType: GENERAL_BROKER_ACCOUNT_TYPES,
    isPopular: true,
    parentBroker: 'interactive-brokers',
    notes: 'Professional-grade multi-asset broker for US markets. Choice of Fixed or Tiered pricing with transparent pass-through fees. All-in costs include commission + exchange/clearing + regulatory fees.',
  },

  {
    id: 'apex-tradovate',
    name: 'Apex (Tradovate)',
    shortName: 'Apex Tradovate',
    assetClasses: ['futures' as const satisfies AssetClassType],
    accountType: 'prop',
    supportedPropFirms: ['apex'],
    isPopular: true,
    parentBroker: 'tradovate',
    notes: 'Apex Trader Funding using Tradovate platform. $85/month fee includes platform and data. All-in commission rates include exchange, clearing, and NFA fees. Same rates for eval and funded accounts.',
  },

  {
    id: 'apex-rithmic',
    name: 'Apex (Rithmic)',
    shortName: 'Apex Rithmic',
    assetClasses: ['futures' as const satisfies AssetClassType],
    accountType: 'prop',
    supportedPropFirms: ['apex'],
    isPopular: true,
    parentBroker: 'rithmic',
    notes: 'Apex Trader Funding using Rithmic platform via NinjaTrader. $85/month or lifetime activation fees. All-in commission rates include exchange, clearing, and NFA fees. Same rates for eval and funded accounts.',
  },

  {
    id: 'tradeify-tradovate',
    name: 'Tradeify (Tradovate)',
    shortName: 'Tradeify Tradovate',
    assetClasses: ['futures' as const satisfies AssetClassType],
    accountType: 'prop',
    supportedPropFirms: ['tradeify'],
    isPopular: true,
    parentBroker: 'tradovate',
    notes: 'Tradeify prop firm using Tradovate platform. No monthly fee. All-in commission rates include exchange, clearing, and NFA fees. Comprehensive futures coverage with transparent total costs.',
  },

  {
    id: 'ftmo',
    name: 'FTMO',
    shortName: 'FTMO',
    assetClasses: ['forex' as const satisfies AssetClassType, 'cfd' as const satisfies AssetClassType, 'crypto' as const satisfies AssetClassType, 'stocks' as const satisfies AssetClassType],
    accountType: 'prop',
    supportedPropFirms: ['ftmo'],
    isPopular: true,
    notes: 'Leading forex/CFD prop firm supporting MT4, MT5, and cTrader platforms with identical fee structures. $3 round-trip on forex, percentage-based fees on metals/equities, zero commission on indices/crypto. Swap fees vary by instrument.',
  },

  {
    id: 'topstep-x',
    name: 'TopStep (TopStepX)',
    shortName: 'TopStep X',
    assetClasses: ['futures' as const satisfies AssetClassType],
    accountType: 'prop',
    supportedPropFirms: ['topstep'],
    isPopular: true,
    parentBroker: 'topstep',
    notes: 'TopStep proprietary commission-free platform. Only NFA and clearing fees apply ($0.74-$4.30 round-trip). Level 1 data included free, Level 2 optional at $34.25/month.',
  },

  {
    id: 'topstep-tradovate',
    name: 'TopStep (Tradovate)',
    shortName: 'TopStep Tradovate',
    assetClasses: ['futures' as const satisfies AssetClassType],
    accountType: 'prop',
    supportedPropFirms: ['topstep'],
    isPopular: true,
    parentBroker: 'topstep',
    notes: 'TopStep using Tradovate or TradingView platform. All-in rates include $1.18 commission plus exchange, clearing, and NFA fees ($1.34-$4.80+ round-trip total).',
  },

  {
    id: 'topstep-ninjatrader',
    name: 'TopStep (NinjaTrader)',
    shortName: 'TopStep Ninja',
    assetClasses: ['futures' as const satisfies AssetClassType],
    accountType: 'prop',
    supportedPropFirms: ['topstep'],
    isPopular: false,
    parentBroker: 'topstep',
    notes: 'TopStep using NinjaTrader, ATAS, or Rithmic platforms. All-in rates include $1.06 commission plus $0.50 data fee, exchange, clearing, and NFA fees ($1.42-$4.80+ round-trip total).',
  },

  {
    id: 'paper-trading',
    name: 'Paper Trading',
    shortName: 'Paper Trading',
    assetClasses: [
      'futures' as const satisfies AssetClassType,
      'cfd' as const satisfies AssetClassType,
      'forex' as const satisfies AssetClassType,
      'options' as const satisfies AssetClassType,
      'stocks' as const satisfies AssetClassType,
      'bonds' as const satisfies AssetClassType,
      'crypto' as const satisfies AssetClassType,
      'funds' as const satisfies AssetClassType
    ],
    accountType: 'paper',
    isPopular: true,
    notes: 'Simulated trading environment with no real money at risk. All asset classes available for practice trading with zero fees and commissions.',
  }

];


/**
 * Get all general purpose brokers (personal-like account types)
 */
export const getPersonalAccountBrokers = (): BrokerCatalogEntry[] => {
  return BROKER_CATALOG_DATA.filter(broker => 
    Array.isArray(broker.accountType) || broker.accountType === 'personal'
  );
};

/**
 * Get all prop firm brokers
 */
export const getPropFirmBrokers = (): BrokerCatalogEntry[] => {
  return BROKER_CATALOG_DATA.filter(broker => broker.accountType === 'prop');
};

/**
 * Get all paper trading brokers
 */
export const getPaperTradingBrokers = (): BrokerCatalogEntry[] => {
  return BROKER_CATALOG_DATA.filter(broker => broker.accountType === 'paper');
};

/**
 * Get brokers supporting specific prop firm
 */
export const getBrokersByPropFirm = (propFirmId: string): BrokerCatalogEntry[] => {
  return BROKER_CATALOG_DATA.filter(broker => 
    broker.supportedPropFirms?.includes(propFirmId)
  );
};

/**
 * Get brokers supporting specific asset class
 */
export const getBrokersByAssetClass = (assetClass: string): BrokerCatalogEntry[] => {
  return BROKER_CATALOG_DATA.filter(broker => 
    broker.assetClasses.includes(assetClass as AssetClassType)
  );
};

/**
 * Get popular brokers only
 */
export const getPopularBrokers = (): BrokerCatalogEntry[] => {
  return BROKER_CATALOG_DATA.filter(broker => broker.isPopular);
};

/**
 * Find broker by ID
 */
export const findBrokerById = (brokerId: string): BrokerCatalogEntry | undefined => {
  return BROKER_CATALOG_DATA.find(broker => broker.id === brokerId);
};